<template>
  <div class="wizard-mobile">
    <!-- Fixed header: back + step counter + progress -->
    <div class="wizard-mobile__header">
      <div class="wizard-mobile__top-row">
        <button class="wizard-mobile__back" type="button" @click="handleBack">
          <i class="fa-regular fa-chevron-left"></i>
          {{ $t('goBackTournaments') }}
        </button>
        <div class="wizard-mobile__step-info">
          <span class="wizard-mobile__step-counter">{{ currentStep }}/{{ totalSteps }}</span>
          <button class="wizard-mobile__hint-trigger" type="button" @click="hintOpen = true">
            <v-icon size="16" color="#9A9A9A">fa-regular fa-circle-info</v-icon>
          </button>
        </div>
      </div>
      <div class="wizard-mobile__step-name">{{ currentStepInfo.name }}</div>
      <div class="wizard-mobile__progress-track">
        <div class="wizard-mobile__progress-fill" :style="{ width: progressPercent + '%' }"></div>
      </div>
    </div>

    <!-- Scrollable body -->
    <div class="wizard-mobile__body">
      <!-- SFB-1289: presets first, the remaining steps are unchanged -->
      <TournamentPreset
        v-if="currentStep === 1"
        :key="1"
        :stepActive="true"
        :hideMeta="true"
        :setNextBtnDisabled="setNextBtnDisabled"
      />
      <TournamentBasicInfo
        v-if="currentStep === 2"
        :key="2"
        :stepActive="true"
        :hideMeta="true"
        :setNextBtnDisabled="setNextBtnDisabled"
        :clubId="clubId"
        :allowBrokerCommunityPick="allowBrokerCommunityPick"
      />
      <TournamentDetails
        v-if="currentStep === 3"
        :key="3"
        :stepActive="true"
        :hideMeta="true"
        :setNextBtnDisabled="setNextBtnDisabled"
        :clubId="clubId"
      />
      <ParticipantSettings
        v-if="currentStep === 4"
        :key="4"
        :stepActive="true"
        :hideMeta="true"
        :setNextBtnDisabled="setNextBtnDisabled"
        :clubId="clubId"
      />
      <TournamentRules
        v-if="currentStep === 5"
        :key="5"
        :stepActive="true"
        :hideMeta="true"
        :setNextBtnDisabled="setNextBtnDisabled"
        :clubId="clubId"
      />
      <TournamentAwards
        v-if="currentStep === 6"
        :key="6"
        :stepActive="true"
        :hideMeta="true"
        :setNextBtnDisabled="setNextBtnDisabled"
      />
      <TournamentSummary
        v-if="currentStep === 7"
        :key="7"
        :stepActive="true"
        :hideMeta="true"
        :setNextBtnDisabled="setNextBtnDisabled"
      />
    </div>

    <!-- Fixed bottom nav -->
    <div class="wizard-mobile__nav">
      <button
        v-if="currentStep > 1"
        class="wizard-mobile__nav-prev"
        type="button"
        @click="prevStep"
      >
        <i class="fa-regular fa-chevron-left"></i>
        {{ $t('prevStep') }}
      </button>
      <button
        class="wizard-mobile__nav-next"
        type="button"
        :disabled="nxtBtnDisabled || loading"
        @click="nextStep"
      >
        <span v-if="currentStep < totalSteps">{{ $t('nextStep') }}</span>
        <span v-else>{{ $t('createTournament_Wizard') }}</span>
        <i v-if="currentStep < totalSteps" class="fa-regular fa-chevron-right"></i>
      </button>
    </div>

    <!-- Hint bottom sheet: step description -->
    <v-bottom-sheet v-model="hintOpen">
      <v-sheet class="wizard-mobile__hint-sheet">
        <div class="wizard-mobile__hint-top">
          <span class="wizard-mobile__hint-title">{{ currentStepInfo.name }}</span>
          <v-btn icon small @click="hintOpen = false">
            <v-icon size="16">fa-regular fa-xmark</v-icon>
          </v-btn>
        </div>
        <p class="wizard-mobile__hint-body">{{ currentStepInfo.description }}</p>
      </v-sheet>
    </v-bottom-sheet>

    <LoadingOverlay style="position: fixed;" :loading="loading" />
    <v-snackbar :color="msgType === 'success' ? 'success' : 'error'" v-model="showMsg">
      {{ msgTxt }}
    </v-snackbar>
  </div>
</template>

<script>
import { mapMutations } from 'vuex';
import LoadingOverlay from '../../../../../components/LoadingOverlay.vue';
import TournamentPreset from './steps/TournamentPreset.vue';
import TournamentBasicInfo from './steps/TournamentBasicInfo.vue';
import TournamentDetails from './steps/TournamentDetails.vue';
import ParticipantSettings from './steps/ParticipantSettings.vue';
import TournamentRules from './steps/TournamentRules.vue';
import TournamentAwards from './steps/TournamentAwards.vue';
import TournamentSummary from './steps/TournamentSummary.vue';
import TournamentRepository from '@/repositories/tournament';
import { formatTournamentRuleValueForApi } from './tournamentRuleUtils';
import { buildUtcDateTime } from '@/utils/tournamentDateTime';

export default {
  name: 'WizardMobile',

  components: {
    LoadingOverlay,
    TournamentPreset,
    TournamentBasicInfo,
    TournamentDetails,
    ParticipantSettings,
    TournamentRules,
    TournamentAwards,
    TournamentSummary,
  },

  props: {
    fromButton: Boolean,
    hideWizard: { type: Function, required: true },
    clubId: { type: [Number, String], default: null },
    allowBrokerCommunityPick: { type: Boolean, default: false },
    // Set when the duplicate flow prefilled the form — skip the reset on open.
    prefillForm: { type: Boolean, default: false },
  },

  data() {
    return {
      currentStep: 1,
      totalSteps: 7,
      nxtBtnDisabled: true,
      loading: false,
      showMsg: false,
      msgType: 'success',
      msgTxt: '',
      hintOpen: false,
    };
  },

  computed: {
    progressPercent() {
      return ((this.currentStep - 1) / (this.totalSteps - 1)) * 100;
    },

    currentStepInfo() {
      const steps = [
        { name: this.$t('title_TournamentPreset'),  description: this.$t('subtitle_TournamentPreset') },
        { name: this.$t('firstSteps'),              description: this.$t('addBasicInfo') },
        { name: this.$t('title_TournamentDetails'), description: this.$t('subtitle_TournamentDetails') },
        { name: this.$t('title_ParticipantSettings'), description: this.$t('subtitle_ParticipantSettings') },
        { name: this.$t('title_TournamentRules'),   description: this.$t('subtitle_TournamentRules') },
        { name: this.$t('title_TournamentAwards'),  description: this.$t('subtitle_TournamentAwards') },
        { name: this.$t('lastCheck'),               description: this.$t('confirmDetails') },
      ];
      return steps[this.currentStep - 1] || steps[0];
    },
  },

  created() {
    if (!this.fromButton) {
      this.$router.push({ name: 'funding-simulation-home' });
    }
    if (!this.prefillForm) {
      this.resetTournamentForm();
    } else {
      // A duplicated competition is already fully specified: skip the preset step.
      this.currentStep = 2;
    }
  },

  methods: {
    ...mapMutations(['resetTournamentForm']),

    setNextBtnDisabled(disabled) {
      this.nxtBtnDisabled = disabled;
    },

    handleBack() {
      this.hideWizard();
      this.currentStep = 1;
      this.resetTournamentForm();
    },

    async nextStep() {
      if (this.currentStep < this.totalSteps) {
        this.currentStep++;
      } else {
        await this.createTournament();
      }
    },

    prevStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
      }
    },

    async createTournament() {
      this.loading = true;
      try {
        const form = this.$store.state.tournamentForm;
        const startDateTime = buildUtcDateTime(
          form.basicInfo.startDate,
          form.basicInfo.startTime || '09:00'
        );
        const endDateTime = buildUtcDateTime(
          form.basicInfo.endDate,
          form.basicInfo.endTime || '18:00'
        );

        const creatorClubId = form.basicInfo.selectedClubId
          || this.$store.state?.clubMember?.club?.id
          || this.clubId;

        const tournamentData = {
          // SFB-1289: lets the backend apply preset defaults the wizard does not model.
          presetKey: form.presetKey || null,
          withWoocomerce: form.details.selfManaged,
          tournament: {
            creator: { id: creatorClubId },
            name: form.basicInfo.name,
            startTime: startDateTime.toISOString(),
            endTime: endDateTime.toISOString(),
            createdAt: new Date().toISOString(),
            tournamentVisibility: form.details.visibility,
            tournamentRankingType: form.participants.rankingType,
            initialBalance: form.initialBalance,
            antiSpoiler: false,
            inviteOnly: form.participants.inviteOnly,
          },
          tournamentSettings: {
            restrictedAccountType: form.details.tournamentType,
            faqsUrl: form.basicInfo.faqsLink || '',
            tournamentVisibility: form.details.visibility,
            tournamentRankingType: form.participants.rankingType,
            initialBalance: parseFloat(form.initialBalance),
            daysPostRegister: this.resolveRegistrationDays(form.participants, startDateTime, endDateTime),
            antiSpoiler: false,
            inviteOnly: form.participants.inviteOnly,
            platform: form.details.platform || null,
            metatraderServerGroupId: form.details.mtGroupId ? { id: form.details.mtGroupId } : null,
            platformServerGroup: form.details.platformGroupId && !form.details.mtGroupId
              ? { id: form.details.platformGroupId }
              : null,
            publicPerformance: form.details.publicPerformance === 1,
            mainLanguage: form.basicInfo.mainLanguage || 'en',
            autoEnroll: form.details.autoEnroll !== undefined ? form.details.autoEnroll : false,
          },
          tournamentRules: form.rules.map(rule => ({
            ruleType: rule.type,
            value: formatTournamentRuleValueForApi(rule),
          })),
          tournamentAwards: form.awards.map((award, index) => ({
            rankNumber: index + 1,
            title: award.name,
            description: award.description,
            estimatedValue: award.value.toString(),
          })),
          selectedFeatures: this.buildSelectedFeatures(form.details.autoEnroll),
        };

        await TournamentRepository.createFullTournamentSettings(tournamentData)
          .then((res) => {
            this.showMsg = true;
            this.msgType = 'success';
            this.msgTxt = this.$t('tournamentMsgSuccess');
            this.setNextBtnDisabled(true);
            setTimeout(() => {
              document.querySelector('html').classList.remove('modal-open');
              if (typeof this.hideWizard === 'function') {
                this.hideWizard();
              }
              this.$router.push({
                name: 'tournament-overview',
                query: { tournamentId: Number.parseInt(res.data.tournament.id) },
              });
            }, 4e3);
          })
          .catch(() => {
            this.showMsg = true;
            this.msgType = 'error';
            this.msgTxt = this.$t('tournamentMsgError');
          });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error creating tournament:', error);
        this.showMsg = true;
        this.msgType = 'error';
        this.msgTxt = this.$t('tournamentMsgError');
      } finally {
        this.loading = false;
      }
    },

    getRegistrationDays(period, startDateTime, endDateTime) {
      const MS_PER_DAY = 1000 * 60 * 60 * 24;
      const tournamentDays = () => {
        if (!startDateTime || !endDateTime) return 0;
        const diffMs = endDateTime.getTime() - startDateTime.getTime();
        if (diffMs <= 0) return 0;
        return Math.ceil(diffMs / MS_PER_DAY);
      };
      switch (period) {
        case 'ONE_DAY':   return 1;
        case 'THREE_DAYS': return 3;
        case 'FIVE_DAYS': return 5;
        case 'ALWAYS':    return tournamentDays();
        default:          return 0;
      }
    },

    resolveRegistrationDays(participants, startDateTime, endDateTime) {
      const maxDays = this.getRegistrationDays('ALWAYS', startDateTime, endDateTime);
      const days = participants.registrationDays ?? this.getRegistrationDays(participants.registrationPeriod, startDateTime, endDateTime);
      if (maxDays <= 0) return days;
      return Math.min(days, maxDays);
    },

    buildSelectedFeatures(autoEnroll) {
      const baseFeatures = [
        'TOURNAMENT_RANKING_AWARD',
        'TOURNAMENT_RANKING_SPONSORED',
        'TOURNAMENT_RANKING_RULES',
        'TOURNAMENT_RANKING_FAQS',
        'TOURNAMENT_RANKING_COUNTER',
        'TOURNAMENT_RANKING_NAME',
        'TOURNAMENT_RANKING_USERNAME',
        'TOURNAMENT_RANKING_ACCOUNT_IDENTIFIER',
        'TOURNAMENT_RANKING_FULL_PAGINATION',
      ];
      if (autoEnroll === false) {
        baseFeatures.push('TOURNAMENT_RANKING_SHOW_REGISTER');
      }
      return baseFeatures;
    },
  },
};
</script>

<style lang="scss" scoped>
.wizard-mobile {
  height: 100%;
  background: var(--v-cardBackground-base, #ffffff);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-family: "Avenir", sans-serif;
}

/* ── Header ─────────────────────────────────────── */
.wizard-mobile__header {
  flex-shrink: 0;
  background: var(--v-cardBackground-base, #ffffff);
  border-bottom: 1px solid #e0e0e0;
  padding: 12px 16px 0 16px;
  /* Push content below device status bar / notch on iOS */
  padding-top: calc(12px + env(safe-area-inset-top, 0px));
}

.wizard-mobile__top-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 36px;
}

.wizard-mobile__back {
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  padding: 0;
  color: #9A9A9A;
  font-size: 13px;
  font-family: inherit;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  i { font-size: 12px; }
}

.wizard-mobile__step-info {
  display: flex;
  align-items: center;
  gap: 4px;
}

.wizard-mobile__step-counter {
  font-size: 13px;
  color: #9A9A9A;
}

.wizard-mobile__hint-trigger {
  background: none;
  border: none;
  padding: 4px;
  display: flex;
  align-items: center;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.wizard-mobile__step-name {
  font-size: 18px;
  font-weight: 700;
  color: var(--v-textPrimary-base, #14161b);
  margin: 10px 0 10px 0;
}

.wizard-mobile__progress-track {
  height: 3px;
  background: #e0e0e0;
  border-radius: 999px;
  overflow: hidden;
  margin: 0 -16px; /* flush to header edges */
}

.wizard-mobile__progress-fill {
  height: 100%;
  background: var(--v-mainGreen-base, #28ea96);
  border-radius: 999px;
  transition: width 0.35s ease;
}

/* ── Body ────────────────────────────────────────── */
.wizard-mobile__body {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 16px;
  background: var(--v-cardBackground-base, #ffffff);

  /* Strip card chrome from step components — not needed in full-screen mobile context */
  ::v-deep .info-card {
    border: none !important;
    border-radius: 0 !important;
    padding: 0 !important;
    background: transparent !important;
    max-height: none !important;
    overflow-y: visible !important;
  }

  /* Give sections a little breathing room */
  ::v-deep .section {
    margin-bottom: 24px;
  }

  ::v-deep .section-title {
    font-size: 15px !important;
  }
}

/* ── Bottom nav ──────────────────────────────────── */
.wizard-mobile__nav {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  padding-bottom: calc(12px + env(safe-area-inset-bottom, 0px));
  background: #ffffff;
  border-top: 1px solid #e0e0e0;
}

.wizard-mobile__nav-prev {
  display: flex;
  align-items: center;
  gap: 6px;
  background: var(--v-cardHeaderBackground-base, #f0f0f0);
  color: var(--v-textPrimary-base, #14161b);
  border: none;
  border-radius: 10px;
  padding: 0 20px;
  height: 48px;
  font-size: 14px;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  -webkit-tap-highlight-color: transparent;

  i { font-size: 12px; }
}

.wizard-mobile__nav-next {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: var(--v-mainGreen-base, #28ea96);
  color: var(--v-mainBlack-base, #14161b);
  border: none;
  border-radius: 10px;
  height: 48px;
  font-size: 15px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: opacity 0.2s;

  i { font-size: 12px; }

  &:disabled {
    background: #c8c8c8;
    color: #888;
    cursor: not-allowed;
  }
}

/* ── Hint bottom sheet ───────────────────────────── */
.wizard-mobile__hint-sheet {
  padding: 20px 20px 32px;
  border-radius: 16px 16px 0 0;
}

.wizard-mobile__hint-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.wizard-mobile__hint-title {
  font-size: 16px;
  font-weight: 700;
  font-family: inherit;
  color: var(--v-textPrimary-base, #14161b);
}

.wizard-mobile__hint-body {
  font-size: 14px;
  line-height: 1.65;
  color: var(--v-textSecondary-base, #666);
  font-family: inherit;
  margin: 0;
}
</style>
